import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam. fetchWorkflows is also re-dispatched with
// the same channel param right after a delete-workflows mutation — unlike a
// background/permissions thunk, a long window here risks silently dropping a
// legitimate refresh after a second, distinct delete a few seconds later. Kept short:
// long enough to absorb same-tick duplicate mounts (the actual bug), short enough to
// never plausibly collide with two separate user-triggered mutations.
const debounceMap = new Map();
const DEBOUNCE_TIME = 600; // 0.6 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);

  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }

  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetWorkflowCaches = () => {
  debounceMap.clear();
};

// Async thunk to fetch workflows
export const fetchWorkflows = createAsyncThunk(
  "workflow/fetchWorkflows",
  async (channel = "whatsapp", { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce(`fetchWorkflows_${channel}`)) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const params = channel === "qr" ? { channel: "qr" } : channel === "instagram" ? { channel: "instagram" } : channel === "facebook" ? { channel: "facebook" } : channel === "website" ? { channel: "website" } : channel === "shopify" ? { channel: "shopify" } : channel === "whatsapp_web" ? { channel: "whatsapp_web" } : {};
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/workflow/getworkflows`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );
      return response.data; // Should be the workflows array
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Async thunk to create a workflow
export const createWorkflow = createAsyncThunk(
  "workflow/createWorkflow",
  async (workflowData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/workflow/createWorkflow`,
        workflowData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data; // Should be the created workflow
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Async thunk to fetch a workflow by id
export const fetchWorkflowById = createAsyncThunk(
  "workflow/fetchWorkflowById",
  async (id, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/workflow/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Async thunk to fetch triggers
export const fetchTriggers = createAsyncThunk(
  "workflow/fetchTriggers",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/workflow/triggers`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Async thunk to fetch actions
export const fetchActions = createAsyncThunk(
  "workflow/fetchActions",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/workflow/actions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Async thunk to toggle workflow status
export const toggleWorkflowStatus = createAsyncThunk(
  "workflow/toggleWorkflowStatus",
  async ({ id, status }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/workflow/turnOnOff/${id}`,
        { status },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.workflow;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

const initialState = {
  workflows: [],
  loading: false,
  error: null,
  selectedWorkflow: null,
  triggers: [],
  actions: [],
  toggleLoading: false, // Add loading state for toggle button
};


const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setSelectedWorkflow: (state, action) => {
      state.selectedWorkflow = action.payload;
      
      // Ensure branches are properly initialized
      if (state.selectedWorkflow && state.selectedWorkflow.currentVersion) {
        if (!state.selectedWorkflow.currentVersion.branches) {
          state.selectedWorkflow.currentVersion.branches = { start: [] };
          console.log('setSelectedWorkflow: Initialized branches object');
        }
      }
    },
    addNodeToBranch: (state, action) => {
      const { branchId, node, nodeIndex } = action.payload;
      
      // Ensure selectedWorkflow exists
      if (!state.selectedWorkflow) {
        console.error('addNodeToBranch: selectedWorkflow is null');
        return;
      }
      
      // Ensure currentVersion exists
      if (!state.selectedWorkflow.currentVersion) {
        console.error('addNodeToBranch: currentVersion is null');
        return;
      }
      
      // Ensure branches object exists
      if (!state.selectedWorkflow.currentVersion.branches) {
        state.selectedWorkflow.currentVersion.branches = { start: [] };
      }
      
      if (!state.selectedWorkflow.currentVersion.branches[branchId]) {
        state.selectedWorkflow.currentVersion.branches[branchId] = [];
      }
      
      if (typeof nodeIndex === 'number' && nodeIndex >= 0 && nodeIndex <= state.selectedWorkflow.currentVersion.branches[branchId].length) {
        state.selectedWorkflow.currentVersion.branches[branchId].splice(nodeIndex, 0, node);
        console.log(`addNodeToBranch: Added node to branch ${branchId} at index ${nodeIndex}`);
      } else {
        state.selectedWorkflow.currentVersion.branches[branchId].push(node);
        console.log(`addNodeToBranch: Added node to end of branch ${branchId}`);
      }
    },
    updateNodeInBranch: (state, action) => {
      const { branchId, nodeId, updates } = action.payload;
      
      // Ensure selectedWorkflow exists
      if (!state.selectedWorkflow) {
        console.error('updateNodeInBranch: selectedWorkflow is null');
        return;
      }
      
      // Ensure currentVersion exists
      if (!state.selectedWorkflow.currentVersion) {
        console.error('updateNodeInBranch: currentVersion is null');
        return;
      }
      
      // Ensure branches object exists
      if (!state.selectedWorkflow.currentVersion.branches) {
        console.error('updateNodeInBranch: branches is null');
        return;
      }
      
      const branch = state.selectedWorkflow.currentVersion.branches[branchId];
      if (branch) {
        const idx = branch.findIndex(n => n.id === nodeId);
        if (idx !== -1) {
          // Clean the updates to remove branchId and nodeId before storing
          const cleanUpdates = { ...updates };
          if (cleanUpdates.trigger) {
            const { branchId: _, nodeId: __, ...cleanTrigger } = cleanUpdates.trigger;
            cleanUpdates.trigger = cleanTrigger;
          }
          if (cleanUpdates.action) {
            const { branchId: _, nodeId: __, ...cleanAction } = cleanUpdates.action;
            cleanUpdates.action = cleanAction;
          }
          
          branch[idx] = { ...branch[idx], ...cleanUpdates };
          console.log(`updateNodeInBranch: Updated node ${nodeId} in branch ${branchId}`);
        } else {
          console.error(`updateNodeInBranch: Node ${nodeId} not found in branch ${branchId}`);
        }
      } else {
        console.error(`updateNodeInBranch: Branch ${branchId} not found`);
      }
    },
    setNodeNextBranches: (state, action) => {
      const { branchId, nodeId, next } = action.payload;
      
      // Ensure selectedWorkflow exists
      if (!state.selectedWorkflow) {
        console.error('setNodeNextBranches: selectedWorkflow is null');
        return;
      }
      
      // Ensure currentVersion exists
      if (!state.selectedWorkflow.currentVersion) {
        console.error('setNodeNextBranches: currentVersion is null');
        return;
      }
      
      // Ensure branches object exists
      if (!state.selectedWorkflow.currentVersion.branches) {
        console.error('setNodeNextBranches: branches is null');
        return;
      }
      
      const branch = state.selectedWorkflow.currentVersion.branches[branchId];
      if (branch) {
        const idx = branch.findIndex(n => n.id === nodeId);
        if (idx !== -1) {
          branch[idx].next = next;
          console.log(`setNodeNextBranches: Set next branches for node ${nodeId} in branch ${branchId}`);
        } else {
          console.error(`setNodeNextBranches: Node ${nodeId} not found in branch ${branchId}`);
        }
      } else {
        console.error(`setNodeNextBranches: Branch ${branchId} not found`);
      }
    },
    addBranch: (state, action) => {
      const { branchId } = action.payload;
      
      // Ensure selectedWorkflow exists
      if (!state.selectedWorkflow) {
        console.error('addBranch: selectedWorkflow is null');
        return;
      }
      
      // Ensure currentVersion exists
      if (!state.selectedWorkflow.currentVersion) {
        console.error('addBranch: currentVersion is null');
        return;
      }
      
      // Ensure branches object exists
      if (!state.selectedWorkflow.currentVersion.branches) {
        state.selectedWorkflow.currentVersion.branches = { start: [] };
      }
      
      // Create the branch if it doesn't exist
      if (!state.selectedWorkflow.currentVersion.branches[branchId]) {
        state.selectedWorkflow.currentVersion.branches[branchId] = [];
        console.log(`addBranch: Created branch ${branchId}`);
      } else {
        console.log(`addBranch: Branch ${branchId} already exists`);
      }
    },
    removeBranch: (state, action) => {
      const { branchId } = action.payload;
      
      // Ensure selectedWorkflow exists
      if (!state.selectedWorkflow) {
        console.error('removeBranch: selectedWorkflow is null');
        return;
      }
      
      // Ensure currentVersion exists
      if (!state.selectedWorkflow.currentVersion) {
        console.error('removeBranch: currentVersion is null');
        return;
      }
      
      // Ensure branches object exists
      if (!state.selectedWorkflow.currentVersion.branches) {
        console.error('removeBranch: branches is null');
        return;
      }
      
      // Remove the branch if it exists
      if (state.selectedWorkflow.currentVersion.branches[branchId]) {
        delete state.selectedWorkflow.currentVersion.branches[branchId];
        console.log(`removeBranch: Removed branch ${branchId}`);
      } else {
        console.log(`removeBranch: Branch ${branchId} does not exist`);
      }
    },
    updateActionConfig: (state, action) => {
      // action.payload: { actionId, newConfig }
      const { actionId, newConfig } = action.payload;
      state.actions = state.actions.map(categoryObj => ({
        ...categoryObj,
        actions: categoryObj.actions.map(act => {
          if ((act._id || act.id || act.name) === actionId) {
            return { ...act, config: newConfig };
          }
          return act;
        })
      }));
    },
    removeNodeFromBranch: (state, action) => {
      const { branchId, nodeId } = action.payload;
      if (!state.selectedWorkflow) {
        console.error('removeNodeFromBranch: selectedWorkflow is null');
        return;
      }
      if (!state.selectedWorkflow.currentVersion) {
        console.error('removeNodeFromBranch: currentVersion is null');
        return;
      }
      if (!state.selectedWorkflow.currentVersion.branches) {
        console.error('removeNodeFromBranch: branches is null');
        return;
      }
      const branch = state.selectedWorkflow.currentVersion.branches[branchId];
      if (branch) {
        const idx = branch.findIndex(n => n.id === nodeId || n._id === nodeId);
        if (idx !== -1) {
          branch.splice(idx, 1);
          console.log(`removeNodeFromBranch: Removed node ${nodeId} from branch ${branchId}`);
        } else {
          console.error(`removeNodeFromBranch: Node ${nodeId} not found in branch ${branchId}`);
        }
      } else {
        console.error(`removeNodeFromBranch: Branch ${branchId} not found`);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.loading = false;
        state.workflows = action.payload;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.loading = false;
        // Check if it's a permission error
        if (action.payload && action.payload.message === "Permission denied") {
          state.error = "You don't have permission to view workflows";
        } else if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      .addCase(fetchWorkflowById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedWorkflow = action.payload;
        
        // Ensure branches are properly initialized
        if (state.selectedWorkflow && state.selectedWorkflow.currentVersion) {
          if (!state.selectedWorkflow.currentVersion.branches) {
            state.selectedWorkflow.currentVersion.branches = { start: [] };
            console.log('fetchWorkflowById: Initialized branches object');
          }
        }
      })
      .addCase(fetchWorkflowById.rejected, (state, action) => {
        state.loading = false;
        // Check if it's a permission error
        if (action.payload && action.payload.message === "Permission denied") {
          state.error = "You don't have permission to view workflows";
        } else {
          state.error = action.payload;
        }
      })
      .addCase(fetchTriggers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTriggers.fulfilled, (state, action) => {
        state.loading = false;
        state.triggers = action.payload;
      })
      .addCase(fetchTriggers.rejected, (state, action) => {
        state.loading = false;
        // Check if it's a permission error
        if (action.payload && action.payload.message === "Permission denied") {
          state.error = "You don't have permission to view workflows";
        } else {
          state.error = action.payload;
        }
      })
      .addCase(fetchActions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActions.fulfilled, (state, action) => {
        state.loading = false;
        state.actions = action.payload;
      })
      .addCase(fetchActions.rejected, (state, action) => {
        state.loading = false;
        // Check if it's a permission error
        if (action.payload && action.payload.message === "Permission denied") {
          state.error = "You don't have permission to view workflows";
        } else {
          state.error = action.payload;
        }
      })
      // Toggle workflow status cases
      .addCase(toggleWorkflowStatus.pending, (state) => {
        state.toggleLoading = true;
        state.error = null;
      })
      .addCase(toggleWorkflowStatus.fulfilled, (state, action) => {
        state.toggleLoading = false;
        // Update the selected workflow status immediately
        if (state.selectedWorkflow && state.selectedWorkflow._id === action.payload._id) {
          state.selectedWorkflow.status = action.payload.status;
        }
        // Update the workflow in the workflows list
        const workflowIndex = state.workflows.findIndex(w => w._id === action.payload._id);
        if (workflowIndex !== -1) {
          state.workflows[workflowIndex].status = action.payload.status;
        }
      })
      .addCase(toggleWorkflowStatus.rejected, (state, action) => {
        state.toggleLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedWorkflow, addNodeToBranch, updateNodeInBranch, setNodeNextBranches, addBranch, removeBranch, updateActionConfig, removeNodeFromBranch } = workflowSlice.actions;
export default workflowSlice.reducer; 